import { X, Search, Loader2, Building2 } from 'lucide-react';
import { useState } from 'react';

const COUNTRY_OPTIONS = [
  'France', 'United Kingdom', 'Germany', 'Spain', 'Italy', 'Belgium',
  'Netherlands', 'Switzerland', 'United States', 'Canada', 'Brazil',
  'Australia', 'Japan', 'South Korea', 'India', 'UAE',
];

const SECTOR_OPTIONS = [
  'Fashion & Beauty', 'Food & Beverage', 'Tech & Digital', 'Gaming',
  'Travel & Tourism', 'Health & Wellness', 'Sport & Fitness',
  'Entertainment', 'Finance', 'Education', 'Automotive', 'Luxury',
  'E-commerce', 'Retail', 'FMCG', 'Telecom',
];

const SIZE_OPTIONS = [
  { value: '1-10', label: '1-10' },
  { value: '11-50', label: '11-50' },
  { value: '51-200', label: '51-200' },
  { value: '201-1000', label: '201-1000' },
  { value: '1001+', label: '1000+' },
];

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (params: SearchParams) => void;
  isSubmitting: boolean;
}

export interface SearchParams {
  countries: string[];
  sectors: string[];
  companySize: string;
  quantity: number;
  languages: string[];
  companyName?: string;
}

export default function SearchModal({ isOpen, onClose, onSubmit, isSubmitting }: SearchModalProps) {
  const [countries, setCountries] = useState<string[]>(['France']);
  const [sectors, setSectors] = useState<string[]>([]);
  const [companySize, setCompanySize] = useState('');
  const [quantity, setQuantity] = useState(500);
  const [langFR, setLangFR] = useState(true);
  const [langEN, setLangEN] = useState(true);
  const [companyName, setCompanyName] = useState('');
  const [searchMode, setSearchMode] = useState<'criteria' | 'company'>('criteria');

  if (!isOpen) return null;

  const handleSubmit = () => {
    const languages: string[] = [];
    if (langFR) languages.push('FR');
    if (langEN) languages.push('EN');
    onSubmit({
      countries: searchMode === 'company' && countries.length === 0 ? [] : countries,
      sectors,
      companySize,
      quantity,
      languages,
      companyName: searchMode === 'company' ? companyName.trim() : undefined,
    });
  };

  const toggleCountry = (c: string) => {
    setCountries((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);
  };

  const toggleSector = (s: string) => {
    setSectors((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Nouvelle recherche</h2>
            <p className="text-sm text-gray-500 mt-0.5">Trouver des decideurs influence</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Mode de recherche</label>
            <div className="flex gap-2">
              <button
                onClick={() => setSearchMode('criteria')}
                className={`flex-1 px-4 py-2.5 text-sm rounded-xl border transition-colors font-medium ${
                  searchMode === 'criteria'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                }`}
              >
                Par criteres
              </button>
              <button
                onClick={() => setSearchMode('company')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm rounded-xl border transition-colors font-medium ${
                  searchMode === 'company'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                }`}
              >
                <Building2 className="w-4 h-4" />
                Par marque / entreprise
              </button>
            </div>
          </div>

          {searchMode === 'company' && (
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Nom de la marque ou domaine</label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Ex: Nike, L'Oreal, apple.com..."
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
              <p className="text-xs text-gray-400 mt-1.5">Entrez un nom de marque ou un domaine pour trouver les decideurs de cette entreprise</p>
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Pays{searchMode === 'company' ? ' (optionnel)' : ''}
            </label>
            <div className="flex flex-wrap gap-2">
              {COUNTRY_OPTIONS.map((c) => (
                <button
                  key={c}
                  onClick={() => toggleCountry(c)}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                    countries.includes(c)
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Secteurs (optionnel)</label>
            <div className="flex flex-wrap gap-2">
              {SECTOR_OPTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => toggleSector(s)}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                    sectors.includes(s)
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Taille d'entreprise (optionnel)</label>
            <div className="flex flex-wrap gap-2">
              {SIZE_OPTIONS.map((s) => (
                <button
                  key={s.value}
                  onClick={() => setCompanySize(companySize === s.value ? '' : s.value)}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                    companySize === s.value
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {s.label} employes
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Quantite de leads</label>
            <input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(Number(e.target.value) || 100)}
              min={50}
              max={5000}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Langues cible</label>
            <div className="flex gap-3">
              <button
                onClick={() => setLangFR(!langFR)}
                className={`px-4 py-2 text-sm rounded-lg border transition-colors ${
                  langFR ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200'
                }`}
              >
                Francais
              </button>
              <button
                onClick={() => setLangEN(!langEN)}
                className={`px-4 py-2 text-sm rounded-lg border transition-colors ${
                  langEN ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200'
                }`}
              >
                English
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-100">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || (searchMode === 'criteria' && countries.length === 0) || (searchMode === 'company' && !companyName.trim())}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Lancement...
              </>
            ) : (
              <>
                <Search className="w-4 h-4" />
                Lancer la recherche
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
